import type {
  ConfigurationChangeset,
  ConfigurationPackage,
  EnumMetaTypeOutput,
  IsStrictlyMoreGeneralPairOutput,
  MetaModelOutput,
  MetaTypeOutput,
  ModelMetaTypeOutput,
  SimpleMetaTypeOutput,
} from '@festina/configuration-general-types/configuration'
import {
  LayerKey,
  OperationLayerMap,
  OperationLayerSet,
  resolveTopologicalPathForBlock,
  type Tree,
} from '@festina-life/api'
import {
  assertNotEmpty,
  type NonEmptyArray,
  notEmpty,
  type Optional,
} from '@festina-life/core'

import {
  type Cardinality,
  type EnumMetaType,
  isModelMetaType,
  type MetaModel,
  type MetaType,
  MetaTypes,
  type ModelMetaType,
  type PropertyDescriptor,
  type SimpleMetaType,
} from '../../../types/metaModel'
import type {
  ConfigurationItemsByBlock,
  ConfigurationItemsByLayer,
} from '../../../workspace/operationsContext'
import type { IndexedRegisteredTenantCreateOperations } from '../../registeredTenantCreateOperations/types'

const isEnumMetaTypeOutput = (
  metaTypeOutput: MetaTypeOutput,
): metaTypeOutput is EnumMetaTypeOutput =>
  metaTypeOutput._type === 'EnumMetaTypeOutput'

const isSimpleMetaTypeOutput = (
  metaTypeOutput: MetaTypeOutput,
): metaTypeOutput is SimpleMetaTypeOutput =>
  metaTypeOutput._type === 'SimpleMetaTypeOutput'

const isModelMetaTypeOutput = (
  metaTypeOutput: MetaTypeOutput,
): metaTypeOutput is ModelMetaTypeOutput =>
  metaTypeOutput._type === 'ModelMetaTypeOutput'

const BUILTIN_SIMPLE_TYPES: SimpleMetaTypeOutput[] = [
  { _type: 'SimpleMetaTypeOutput', typeName: 'Date' },
  { _type: 'SimpleMetaTypeOutput', typeName: 'DateTime' },
  { _type: 'SimpleMetaTypeOutput', typeName: 'Decimal' },
  { _type: 'SimpleMetaTypeOutput', typeName: 'Duration' },
  { _type: 'SimpleMetaTypeOutput', typeName: 'Object' },
  // These seem to be odd ones out (they are not present in Configr but they are in Cloud)
  { _type: 'SimpleMetaTypeOutput', typeName: 'SMap' },
  { _type: 'SimpleMetaTypeOutput', typeName: 'model' },
  { _type: 'SimpleMetaTypeOutput', typeName: 'byte[]' },
]

export const transformLayerMetaModels = (
  metaModels: Optional<Map<Optional<string>, MetaModelOutput>>,
): Map<Optional<string>, MetaModel> =>
  new Map(
    Array.from(metaModels?.entries() || []).map(([layer, metaModel]) => [
      layer,
      transformMetaModel(metaModel),
    ]),
  )

export const transformMetaModel = (
  metaModel: Optional<MetaModelOutput>,
): MetaModel => {
  const nonNullTypes =
    (metaModel?.types?.filter(Boolean) as MetaTypeOutput[]) || []

  const typeMap = new Map(
    nonNullTypes.map((t: MetaTypeOutput) => [
      t?.typeName as string,
      generateType(t),
    ]),
  )

  // Make sure built-in types are present (not when running in cloud)
  for (const type of BUILTIN_SIMPLE_TYPES) {
    const typeName = type.typeName!

    if (!typeMap.has(typeName)) {
      typeMap.set(typeName, generateType(type))
    }
  }

  typeMap.forEach((type) => {
    if (isModelMetaType(type)) {
      if (type.superTypeName) {
        const superType = typeMap.get(type.superTypeName)
        assertNotEmpty(superType)
        type.superType = superType
      }

      type.subTypes = type.subTypeNames.map((name: string) => {
        const subType = typeMap.get(name)
        assertNotEmpty(subType)

        return subType
      })

      if (type.modelTypeName === 'Abstract') {
        // The abstract type is not actually returned (so it won't be present in the typesMap).
        type.modelTypeName = null
      } else if (type.modelTypeName) {
        const modelType = typeMap.get(type.modelTypeName)

        if (isModelMetaType(modelType)) {
          type.modelType = modelType

          if (
            modelType.templateType !== undefined &&
            modelType.templateType !== type
          ) {
            throw new Error('Trying to overwrite a different templateType')
          }
          modelType.templateType = type
          modelType.templateTypeName = type.typeName
        }
      }

      const configurationDetails = type.configurationDetails

      if (configurationDetails) {
        if (configurationDetails.environmentTypeName) {
          const environmentType = typeMap.get(
            configurationDetails.environmentTypeName,
          )

          if (environmentType === undefined) {
            // Likely BaseEnvironment in Cloud
            configurationDetails.environmentTypeName = null
          } else if (isModelMetaType(environmentType)) {
            configurationDetails.environment = environmentType
          }
        }

        configurationDetails.defaultLibraryTypeNames.forEach(
          (libraryTypeName) => {
            const libraryType = typeMap.get(libraryTypeName)

            if (isModelMetaType(libraryType)) {
              configurationDetails.defaultLibraries.push(libraryType)
            }
          },
        )
      }

      type.properties.forEach((propertyDescriptor) => {
        if (propertyDescriptor.typeName) {
          if (propertyDescriptor.typeName.includes('<')) {
            // TODO FIXME we should really have a single type but generics reference more than one
            // The type claims propertyDescriptor.type is always present but that's not true here!
          } else {
            const propertyType = typeMap.get(propertyDescriptor.typeName)

            if (propertyType) {
              propertyDescriptor.type = propertyType
            }
          }
        }

        if (propertyDescriptor.onlyTemplatedTypeName) {
          if (propertyDescriptor.onlyTemplatedTypeName.includes('<')) {
            propertyDescriptor.onlyTemplatedTypeName = null // types with generics not supported
          } else {
            const onlyTemplatedType = typeMap.get(
              propertyDescriptor.onlyTemplatedTypeName,
            )

            if (onlyTemplatedType) {
              propertyDescriptor.onlyTemplatedType = onlyTemplatedType
            }
          }
        }
      })
    }
  })

  return { types: typeMap }
}

const generateEnumMetaType = (
  metaTypeOutput: EnumMetaTypeOutput,
): EnumMetaType => ({
  _type: MetaTypes.EnumMetaType,
  packageName: metaTypeOutput.packageName,
  typeName: metaTypeOutput.typeName!, // checked above
  documentation: metaTypeOutput.documentation ?? '',
  values: metaTypeOutput.values?.filter(notEmpty) ?? [],
})

const generateSimpleMetaType = (
  metaTypeOutput: SimpleMetaTypeOutput,
): SimpleMetaType => ({
  _type: MetaTypes.SimpleMetaType,
  typeName: metaTypeOutput.typeName!, // checked above
  documentation: metaTypeOutput.documentation ?? '',
})

const generateModelMetaType = (
  metaTypeOutput: ModelMetaTypeOutput,
): ModelMetaType => {
  const typeName = metaTypeOutput.typeName! // checked above
  const stereoTypes = metaTypeOutput.stereoTypes?.filter(notEmpty) ?? []
  const subTypeNames = metaTypeOutput.subTypeNames?.filter(notEmpty) ?? []
  const superTypeName = metaTypeOutput.superTypeName

  const configurationDetails = metaTypeOutput.configurationTypeOutput
    ? {
        environmentTypeName:
          metaTypeOutput.configurationTypeOutput?.environment,
        defaultLibraryTypeNames:
          metaTypeOutput.configurationTypeOutput?.defaultImportedLibraries?.filter(
            notEmpty,
          ) ?? [],
        defaultLibraries: [],
        functions:
          metaTypeOutput.configurationTypeOutput.functions?.filter(notEmpty) ??
          [],
        environment: null,
      }
    : undefined

  const suffix = ['SubTemplate', 'Template'].find((suffix) =>
    typeName.endsWith(suffix),
  )
  const modelTypeName = suffix
    ? typeName.substring(0, typeName.length - suffix.length)
    : undefined

  const properties =
    metaTypeOutput.properties?.filter(notEmpty).map((p): PropertyDescriptor => {
      assertNotEmpty(p.typeName)

      return {
        typeName: p.typeName,
        type: undefined as unknown as MetaType, // overwritten later
        documentation: p.documentation ?? '',
        onlyTemplatedTypeName: p.configurationDetails?.originalTypeName ?? '',
        onlyTemplatedType: null,
        cardinality: (p.cardinality as Optional<Cardinality>) ?? 'one',
        name: p.name ?? '',
        association:
          (p.association as Optional<PropertyDescriptor['association']>) ??
          'composition',
        stereoTypes: p.stereoTypes?.filter(notEmpty) ?? [],
      }
    }) ?? []

  return {
    _type: MetaTypes.ModelMetaType,
    typeName,
    documentation: metaTypeOutput.documentation ?? '',
    packageName: metaTypeOutput.packageName,
    superType: undefined as unknown as MetaType, // overwritten later
    superTypeName,
    subTypes: [],
    subTypeNames,
    stereoTypes,
    properties,
    modelType: undefined,
    modelTypeName,
    templateType: undefined,
    templateTypeName: undefined,
    configurationDetails,
  } satisfies ModelMetaType
}

const generateType = (metaTypeOutput: MetaTypeOutput): MetaType => {
  if (isEnumMetaTypeOutput(metaTypeOutput)) {
    return generateEnumMetaType(metaTypeOutput)
  }

  if (isModelMetaTypeOutput(metaTypeOutput)) {
    return generateModelMetaType(metaTypeOutput)
  }

  if (isSimpleMetaTypeOutput(metaTypeOutput)) {
    // Until _type is an enum, `metaTypeOutput` is `never` here, so we can't print it's _type.
    // This happens because `type SimpleMetaTypeOutput = MetaTypeOutput`.
    // So the simple meta type must be checked last.
    return generateSimpleMetaType(metaTypeOutput)
  }

  throw new Error(`Expected MetaTypeOutput to be ModelMetaTypeOutput`)
}

/**
 * Transforms items with a IsStrictlyMoreGeneralPairOutput relationship into a hierarchical Tree[] structure.
 * Builds a tree from the isStrictlyMoreGeneralPairs relationships.
 */
export const transformPairsToTree = ({
  items,
  strictlyMoreGeneralPairs,
}: {
  items: Optional<string[]>
  strictlyMoreGeneralPairs: Optional<IsStrictlyMoreGeneralPairOutput[]>
}): Tree[] => {
  if (!items || (items.length > 1 && !strictlyMoreGeneralPairs)) {
    return []
  }

  const safelyStrictlyMoreGeneralPairs = strictlyMoreGeneralPairs ?? []

  // Build a map of all parent -> children relationships from the pairs
  const allRelationships = new Map<string, Set<string>>()
  const allChildren = new Set<string>()

  for (const { lhs, rhs } of safelyStrictlyMoreGeneralPairs) {
    if (!lhs || !rhs) {
      continue
    }

    const parent = lhs
    const child = rhs

    if (!allRelationships.has(parent)) {
      allRelationships.set(parent, new Set())
    }
    allRelationships.get(parent)!.add(child)
    allChildren.add(child)
  }

  // Filter to only direct parent-child relationships
  // A child is a direct child of a parent if there's no intermediate item
  // that is both a child of the parent and a parent of the child
  const directChildrenMap = new Map<string, Set<string>>()

  for (const [parent, children] of allRelationships.entries()) {
    const directChildren = new Set<string>()

    for (const child of children) {
      // Check if there's an intermediate layer
      let isDirect = true
      for (const intermediate of children) {
        if (intermediate === child) {
          continue
        }
        const intermediateChildren = allRelationships.get(intermediate)
        if (intermediateChildren?.has(child)) {
          isDirect = false
          break
        }
      }
      if (isDirect) {
        directChildren.add(child)
      }
    }

    if (directChildren.size > 0) {
      directChildrenMap.set(parent, directChildren)
    }
  }

  // Find root nodes (items that are never children)
  const rootNodes = items.filter((item) => !allChildren.has(item))

  // Helper function to build a tree node
  const buildTreeNode = (itemName: string): Tree => {
    const children = directChildrenMap.get(itemName) || new Set()
    // Sort children according to the topological order in the items array
    const sortedChildren = Array.from(children).sort((a, b) => {
      const indexA = items.indexOf(a)
      const indexB = items.indexOf(b)
      return indexA - indexB
    })

    return {
      name: itemName,
      children: sortedChildren.map(buildTreeNode),
    }
  }

  // Build tree for each root node, sorted by topological order
  return rootNodes
    .sort((a, b) => {
      const indexA = items.indexOf(a)
      const indexB = items.indexOf(b)
      return indexA - indexB
    })
    .map(buildTreeNode)
}

export const getAllLayersOnTopology = ({
  topology,
  allTenants,
  visitedLayers = [],
}: {
  topology: Optional<Tree>
  allTenants: Optional<
    ConfigurationItemsByLayer<IndexedRegisteredTenantCreateOperations>
  >
  visitedLayers?: LayerKey[]
}): LayerKey[] => {
  const layerKey = new LayerKey({ layerId: topology?.name, tenantId: null })

  if (!topology?.children.length) {
    return [
      layerKey,
      ...[...visitedLayers, layerKey]
        .filter((visitedLayer) => !!allTenants?.get(visitedLayer)?.size)
        .flatMap((visitedLayerWithTenantDefinitions) =>
          Array.from(
            allTenants?.get(visitedLayerWithTenantDefinitions)?.values() ?? [],
          ).map(
            (tenant) =>
              new LayerKey({
                layerId: topology?.name,
                tenantId: tenant.tenantId,
              }),
          ),
        ),
    ]
  }

  return [
    layerKey,
    ...topology.children.flatMap((child) =>
      getAllLayersOnTopology({
        topology: child,
        allTenants,
        visitedLayers: [...visitedLayers, layerKey],
      }),
    ),
  ]
}

export const getLeafLayersOnTopology = (layerTopology: Tree): LayerKey[] => {
  if (!layerTopology.children.length) {
    return [new LayerKey({ layerId: layerTopology.name, tenantId: null })]
  }

  return layerTopology.children.flatMap(getLeafLayersOnTopology)
}

export const getTreeNodeByName = (
  nodeName: Optional<string>,
  topology: Tree,
): Optional<Tree> => {
  if (nodeName === topology.name) {
    return topology
  }

  return topology.children.find((child) => getTreeNodeByName(nodeName, child))
}

export const transformServerLayerTopologyToLayerKeys = ({
  layers,
  allTenants,
  blockTopology,
}: {
  layers: Tree[]
  allTenants: Optional<
    ConfigurationItemsByBlock<IndexedRegisteredTenantCreateOperations>
  >
  blockTopology: Tree
}): Map<string, [LayerKey, ...LayerKey[]]> => {
  const allBlocks = collectAllNodesFromTree(blockTopology)
  return new Map<string, [LayerKey, ...LayerKey[]]>(
    allBlocks.map((block) => {
      const currentBlockPath = resolveTopologicalPathForBlock(
        block,
        blockTopology,
      )
      const tenantsOnBlock = currentBlockPath.reduce<
        ConfigurationItemsByLayer<IndexedRegisteredTenantCreateOperations>
      >((acc, block) => {
        const layeredTenantsForBlock = allTenants?.get(block)

        Array.from(layeredTenantsForBlock?.entries() ?? []).forEach(
          ([layerKey, tenantOperations]) => {
            const existentTenantsForLayer =
              acc.get(layerKey) ??
              new Map<string, IndexedRegisteredTenantCreateOperations>()
            acc.set(layerKey, existentTenantsForLayer)

            tenantOperations.forEach((tenantOperation, tenantId) => {
              const currentIndexedTenantOperation =
                existentTenantsForLayer.get(tenantId)

              if (currentIndexedTenantOperation) {
                currentIndexedTenantOperation.operations.push(
                  ...tenantOperation.operations,
                )
                currentIndexedTenantOperation.isDraft =
                  currentIndexedTenantOperation.isDraft ||
                  tenantOperation.isDraft
              } else {
                existentTenantsForLayer.set(tenantId, {
                  ...tenantOperation,
                  operations: [...tenantOperation.operations], // We need to create a new array to avoid mutating the original operations array
                })
              }
            })
          },
        )

        return acc
      }, new OperationLayerMap<Map<string, IndexedRegisteredTenantCreateOperations>>())

      const noLayerKey = new LayerKey({ layerId: null, tenantId: null })

      const layersWithTenants = layers.flatMap((topology) => [
        ...getAllLayersOnTopology({
          topology,
          allTenants: tenantsOnBlock,
        }),
        noLayerKey,
      ]) as [LayerKey, ...LayerKey[]]
      const noLayerWithTenants = getAllLayersOnTopology({
        topology: null,
        allTenants: tenantsOnBlock,
      })

      return [
        block,
        Array.from(
          new OperationLayerSet([
            ...layersWithTenants,
            ...noLayerWithTenants,
          ]).values(),
        ) as [LayerKey, ...LayerKey[]],
      ] // We know there will always be at least the noLayerKey
    }),
  )
}

/**
 * Standard components do not support layering. Therefore we need to flatten the configuration package layers before publishing
 * packages to a component. This function will return a new configuration package with the layers flattened.
 */
export const flattenConfigurationPackageLayersForStandardComponent = (
  configurationPackage: ConfigurationPackage,
) => {
  const changesetTenantMap = new Map<Optional<string>, ConfigurationPackage>()

  configurationPackage.changesets?.filter(notEmpty).forEach((changeset) => {
    const tenantPackage = changesetTenantMap.get(
      changeset.tenantId ?? null,
    ) ?? {
      _type: 'ConfigurationPackage',
      changesets: [],
    }

    assertNotEmpty(tenantPackage.changesets)
    tenantPackage.changesets.push(changeset)

    changesetTenantMap.set(changeset.tenantId ?? null, tenantPackage)
  })

  // Changeset histories are ignored since squashed packages don't contain them and they're not relevant for the standard component.
  return changesetTenantMap.entries().reduce<ConfigurationPackage>(
    (acc, [tenantId, tenantPackage]) => {
      const existentTenantChangeset = acc.changesets
        ?.filter(notEmpty)
        .find((changeset) => changeset.tenantId == tenantId)

      const nonEmptyChangesets =
        (tenantPackage.changesets?.filter(notEmpty) ?? []).map((changeset) => ({
          ...changeset,
          layer: undefined,
        })) ?? [] // layers are not relevant for the standard component, so we remove them

      // For the shared tenant, push all operations into the same changeset
      const reducedChangesets = nonEmptyChangesets.length
        ? (nonEmptyChangesets as NonEmptyArray<ConfigurationChangeset>)
            // @ts-expect-error - we know that the array is non-empty
            .reduce<ConfigurationChangeset>(
              (accTenantChangesets, changeset) => {
                assertNotEmpty(accTenantChangesets.operations)
                assertNotEmpty(changeset.operations)
                accTenantChangesets.operations.push(...changeset.operations)
                return accTenantChangesets
              },
            )
        : undefined

      if (reducedChangesets) {
        if (existentTenantChangeset) {
          existentTenantChangeset.operations?.push(
            ...(reducedChangesets?.operations ?? []),
          )
        } else {
          assertNotEmpty(acc.changesets)
          acc.changesets.push(reducedChangesets)
        }
      }

      return acc
    },
    {
      _type: 'ConfigurationPackage',
      changesets: [],
    },
  )
}

export const collectAllNodesFromTree = (tree: Tree): string[] => [
  tree.name,
  ...tree.children.flatMap(collectAllNodesFromTree),
]

export const getItemDepthInTree = ({
  itemName,
  tree,
  depth = 0,
}: {
  itemName: Optional<string>
  tree: Tree
  depth?: number
}): number | null => {
  if (itemName === tree.name) {
    return depth
  }

  if (!tree.children.length) {
    return null
  }

  for (const child of tree.children) {
    const foundDepth = getItemDepthInTree({
      itemName,
      tree: child,
      depth: depth + 1,
    })
    if (foundDepth) {
      return foundDepth
    }
  }
  return null
}
